/**
 * Moves an open web tab onto a new deploy at the first moment that costs the
 * user nothing.
 *
 * `chunk-reload.ts` only recovers a tab whose old lazy chunk 404s. A tab that
 * already holds every chunk it needs never asks for one, so it keeps running
 * the previous build — bugs included — until someone refreshes by hand. That
 * is how a routing bug fixed by a deploy kept reporting for a day from a
 * single stale `index-*.js`.
 *
 * Every build writes its id to `/version.json` (see `buildVersionPlugin` in
 * vite.config.ts) and bakes the same id into the bundle. The tab polls the
 * file; once the ids differ, the user's NEXT PAGE CHANGE becomes a full page
 * load of the destination instead of a client-side navigation. That is the one
 * moment a reload costs nothing: they asked to leave the screen, so there is no
 * scroll position, half-typed field or open panel to lose, and it reads as a
 * slightly slower navigation rather than the app refreshing under them.
 *
 * A tab that never changes page (a calendar or till left open all day) would
 * never move, so there is one fallback: coming back to a tab that was hidden
 * for `IDLE_RETURN_MS` reloads it in place, before the user has started
 * anything. That fallback is OPT-IN per page (`allowIdleReturnRefresh`), and
 * only for screens that hold nothing in memory worth keeping. Off by default
 * because a hold is easy to forget and the page change never needs one: half a
 * patient intake form, an ad wizard or a graphic wizard keep their answers in
 * component state, and a reload on return wiped them while the person had only
 * gone to another tab to look something up.
 *
 * Either way it stands down while work would be destroyed — a recording, upload
 * or draft (`holdBuildRefresh`), a request mid-write, or an open dialog or
 * sheet, some of which (POS checkout) outlive a route change. It simply takes
 * the next opportunity after that.
 *
 * HOW TO VERIFY A CHANGE HERE. Unit tests and the Playwright spec
 * (apps/app-e2e/src/auth/build-refresh.spec.ts) are necessary and were not
 * sufficient: both passed while `router.navigate()` page changes were silently
 * never handed off, because every test navigated with a mouse click — and
 * again while it was inert for every signed-in user, because every test started
 * on /sign-in, where the router has no redirect on record. Exercise EVERY way
 * of changing page (a link click, `router.navigate()`, an untrusted DOM click),
 * from a session that has been through a redirect, and run
 * `apps/app-e2e/scripts/vercel-build-refresh-sim.mjs` against a real
 * deployment, in a real browser, SIGNED IN, before trusting it. Note that a
 * deploy which changes only tests or other packages keeps the same build id
 * (turbo restores the app's `dist`), so it takes a change to this app's source
 * to produce a swap to test against.
 *
 * Web only. Native builds are moved by Capgo and the app-store gate.
 *
 * TO SWITCH IT OFF IN PRODUCTION: set `WEB_BUILD_REFRESH_DISABLED=true` on the
 * Vercel project. It is read at runtime by `/api/runtime-config`, so it needs
 * no rebuild of the app — but on Vercel an env change only applies to a new
 * deployment, so REDEPLOY after setting it. That redeploy does not itself
 * trigger a refresh (an unchanged app keeps its build id). Tabs already open
 * re-read the switch when they next see a newer build, before arming; new tabs
 * read it at boot. Watch it working (or not) in PostHog: `web_build_refreshed` events, and `app_build` on every event.
 */
import { Capacitor } from '@capacitor/core';

import { RELOAD_MARKER_PARAM } from './chunk-reload';
import { logError } from './log-error';

const POLL_INTERVAL_MS = 5 * 60_000;
const MIN_CHECK_GAP_MS = 10_000;
const IDLE_RETURN_MS = 10 * 60_000;
const RELOADED_FOR_KEY = 'borradh:build-refresh-target';
const RECEIPT_KEY = 'borradh:build-refresh-receipt';
/** How long a navigation waits for the page to unload before carrying on. */
export const HAND_OFF_TIMEOUT_MS = 10_000;

/** The id baked into this bundle. Empty in dev and in tests. */
export const currentBuildId: string =
  typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : '';

export type BuildRefreshTrigger = 'page_change' | 'idle_return';

export interface BuildRefreshReceipt {
  from: string;
  to: string;
  trigger: BuildRefreshTrigger;
}

interface InstallOptions {
  /** Requests whose loss would be a lost write, e.g. `queryClient.isMutating()`. */
  isBusy?: () => boolean;
}

const holds = new Set<symbol>();
/** Pages that have said a reload on return costs them nothing. */
const idleReturnPages = new Set<symbol>();
let installed = false;
let enabled = true;
let isBusy: () => boolean = () => false;
let liveBuildId: string | null = null;
/** Destination of the real navigation in flight, from the router's events. */
let navigatingTo: string | null = null;
/** A hand-off that has been decided and is waiting to be carried out. */
let pendingHandOff: { href: string; pathname: string } | null = null;

/**
 * Keep the tab on its current build while work that a reload would destroy is
 * in progress (recording, upload, unsaved draft). Returns the release. Prefer
 * `useUnsavedWorkGuard`, which pairs this with the `beforeunload` prompt.
 */
export function holdBuildRefresh(): () => void {
  const token = Symbol('build-refresh-hold');
  holds.add(token);
  return () => {
    holds.delete(token);
  };
}

/**
 * Let a tab on this page be reloaded in place when the user comes back after
 * `IDLE_RETURN_MS`. Only for pages whose whole state is on the server or in the
 * URL — a calendar, the till — and that people leave open all day without
 * changing page. Holds, dialogs, in-flight writes and a focused field still
 * stand it down. Returns the release. Prefer `useIdleReturnBuildRefresh`.
 */
export function allowIdleReturnRefresh(): () => void {
  const token = Symbol('build-refresh-idle-return');
  idleReturnPages.add(token);
  return () => {
    idleReturnPages.delete(token);
  };
}

/**
 * Runtime kill switch, fed from `webBuildRefreshDisabled` in the runtime
 * config. Off means: never hand a page change or an idle return to a new
 * build. Holds and checks keep working, so flipping it back on needs nothing.
 */
export function setWebBuildRefreshEnabled(next: boolean): void {
  if (enabled && !next) warn('Disabled by runtime config');
  enabled = next;
}

function warn(message: string, extra?: Record<string, unknown>) {
  console.warn('[app.buildRefresh]', message, extra ?? '');
}

function isTyping(): boolean {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    return true;
  }
  if (el instanceof HTMLInputElement) {
    return !['button', 'checkbox', 'radio', 'submit'].includes(el.type);
  }
  return false;
}

/**
 * Radix dialogs, alert dialogs, sheets and drawers all render this. A safety
 * net for work nobody registered a hold for — not the primary mechanism.
 */
function hasOpenDialog(): boolean {
  return (
    document.querySelector(
      '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
    ) !== null
  );
}

const blocked = (): boolean => holds.size > 0 || isBusy() || hasOpenDialog();

/** One automatic reload per target build: a skewed CDN must not loop us. */
function alreadyReloadedFor(target: string): boolean {
  try {
    return sessionStorage.getItem(RELOADED_FOR_KEY) === target;
  } catch {
    return true;
  }
}

/** Records the decision; the caller then performs the navigation. */
function claimLiveBuild(trigger: BuildRefreshTrigger): boolean {
  if (!enabled || !liveBuildId || alreadyReloadedFor(liveBuildId)) return false;
  try {
    sessionStorage.setItem(RELOADED_FOR_KEY, liveBuildId);
    // The page is about to unload, so the analytics event is sent by the build
    // that boots next — see `consumeBuildRefreshReceipt`.
    const receipt: BuildRefreshReceipt = {
      from: currentBuildId,
      to: liveBuildId,
      trigger,
    };
    sessionStorage.setItem(RECEIPT_KEY, JSON.stringify(receipt));
  } catch {
    // Without the guard we could loop, so stay on this build.
    return false;
  }
  return true;
}

/**
 * A distinct URL, for the reason chunk-reload.ts gives: `reload()` may reuse
 * the stale navigation entry. The marker is stripped on boot.
 */
function freshUrl(href: string): string {
  const url = new URL(href, window.location.href);
  url.searchParams.set(RELOAD_MARKER_PARAM, '1');
  return url.toString();
}

/**
 * Has the router's own history write for this navigation landed yet?
 *
 * The root `beforeLoad` runs BEFORE the router pushes the destination onto
 * history (measured: ~2ms before, for a link click and for `navigate()` alike).
 * Navigating away at that instant leaves the queued push to land as well, and
 * the destination ends up in history twice — Back then appears to do nothing.
 * So wait for the push, then REPLACE the entry it made. The push is flushed on
 * a microtask; the timer fallback is for hosts that throttle (hidden tabs).
 */
async function historyLanded(pathname: string): Promise<boolean> {
  for (let hop = 0; hop < 50; hop++) {
    if (window.location.pathname === pathname) return true;
    await Promise.resolve();
  }
  for (let tick = 0; tick < 20; tick++) {
    await new Promise((resolve) => setTimeout(resolve, 50));
    if (window.location.pathname === pathname) return true;
  }
  return false;
}

async function performHandOff(to: {
  href: string;
  pathname: string;
}): Promise<void> {
  try {
    const landed = await historyLanded(to.pathname);
    // Never landed: nothing of the router's to replace, so add the entry
    // ourselves rather than overwrite the page they are leaving.
    if (landed) window.location.replace(freshUrl(to.href));
    else window.location.assign(freshUrl(to.href));
  } catch (error) {
    logError('app.buildRefresh', error, { feature: 'app' });
  }
}

/**
 * Wire these to the router's `onBeforeLoad` / `onResolved` events (see
 * `connectBuildRefreshToRouter` for why not `onBeforeNavigate`). They are
 * how a real navigation is told apart from a hover/intent PRELOAD, which runs
 * the root `beforeLoad` too but emits no navigation event.
 *
 * Do not try to infer it instead. The router's `preload` flag is always false
 * on the root route (it is resolved per match, and the root match is always
 * active). And history is no guide: `beforeLoad` runs before the push, so
 * "the URL has not moved yet" is true of a preload AND of every real
 * navigation. A guard built on that passed every mouse-click test — a click
 * happens to trigger a second `beforeLoad` after the push — and silently never
 * fired for `router.navigate()`, i.e. for ~230 call sites.
 */
export function noteNavigationStarted(to: {
  href: string;
  pathChanged: boolean;
  /** The router's `fromLocation`; absent on the initial page load. */
  isInitialLoad: boolean;
}): void {
  // The initial load is announced as a navigation too. It is not a page
  // CHANGE: a tab that boots already stale has only just fetched index.html,
  // so reloading it buys nothing and spends the one reload this build gets.
  // Search-param churn (filters, a calendar's date) is not leaving the screen.
  navigatingTo = to.pathChanged && !to.isInitialLoad ? to.href : null;
}

export function noteNavigationSettled(): void {
  navigatingTo = null;
}

/** The slice of the TanStack router this needs — kept structural so it tests. */
interface NavigationEvents {
  subscribe(
    event: 'onBeforeLoad',
    listener: (e: {
      fromLocation?: unknown;
      toLocation: { href: string };
      pathChanged: boolean;
    }) => void
  ): () => void;
  subscribe(event: 'onResolved', listener: () => void): () => void;
}

/**
 * Feeds the router's navigation events in. Its own function, with its own
 * tests, because THREE of this module's bugs lived in exactly this wiring:
 *
 *  - which event means "a real navigation". It is `onBeforeLoad`, NOT
 *    `onBeforeNavigate`: the router skips `onBeforeNavigate` whenever it has a
 *    redirect on record (`if (!this.stores.redirect.get())`), and the signed-in
 *    app always does — sign-in redirects to the dashboard, which redirects to a
 *    branch. So it fired on the sign-in page, where every test started, and
 *    never again after login. `onBeforeLoad` is emitted unconditionally, with
 *    the same payload, and only from `router.load()` — preloads go straight to
 *    `loadMatches` and emit neither;
 *  - that the router announces the INITIAL load (no `fromLocation`) as one;
 *  - and, before events were used at all, the history-based guess described on
 *    `noteNavigationStarted`.
 */
export function connectBuildRefreshToRouter(
  router: NavigationEvents
): () => void {
  const offStart = router.subscribe('onBeforeLoad', (e) =>
    noteNavigationStarted({
      href: e.toLocation.href,
      pathChanged: e.pathChanged,
      isInitialLoad: !e.fromLocation,
    })
  );
  const offEnd = router.subscribe('onResolved', noteNavigationSettled);
  return () => {
    offStart();
    offEnd();
  };
}

/**
 * Called from the root route's `beforeLoad`, which runs for real navigations
 * AND for hover/intent preloads; `noteNavigationStarted` tells them apart. Returns `true` when it has started a full page load of the
 * destination — the caller must then halt the client-side navigation.
 */
export function takePageChangeForBuildRefresh(location: {
  pathname: string;
  href: string;
}): boolean {
  // FAIL OPEN. This runs inside the root route's `beforeLoad`, i.e. on every
  // navigation for every user. Anything thrown here would break navigation
  // app-wide, which is a far worse outcome than a tab staying on an old build.
  try {
    return takePageChange(location);
  } catch (error) {
    logError('app.buildRefresh', error, { feature: 'app' });
    return false;
  }
}

function takePageChange(location: { pathname: string; href: string }): boolean {
  if (!installed) return false;
  // Only the navigation the router announced. Anything else reaching the root
  // `beforeLoad` — a preload, a reload of the current matches — is not one.
  if (navigatingTo === null || location.href !== navigatingTo) return false;
  navigatingTo = null;
  if (blocked()) return false;
  if (!claimLiveBuild('page_change')) return false;
  pendingHandOff = { href: location.href, pathname: location.pathname };
  return true;
}

/**
 * Carries out the hand-off `takePageChangeForBuildRefresh` decided on, and
 * holds the client-side navigation while the page unloads, so no loader of the
 * outgoing build runs — but only for so long. If the unload never happens
 * (something cancelled it), the navigation carries on in the old build rather
 * than sitting on the pending splash forever.
 */
export function waitForBuildHandOff(): Promise<void> {
  const handOff = pendingHandOff;
  pendingHandOff = null;
  if (handOff) void performHandOff(handOff);
  return new Promise((resolve) => {
    setTimeout(() => {
      logError(
        'app.buildRefresh',
        new Error('Hand-off to the new build did not unload the page'),
        { feature: 'app', extra: { from: currentBuildId, to: liveBuildId } }
      );
      resolve();
    }, HAND_OFF_TIMEOUT_MS);
  });
}

/**
 * The refresh that put this tab on its current build, once. Read after
 * analytics is ready so it can be reported as `web_build_refreshed`.
 */
export function consumeBuildRefreshReceipt(): BuildRefreshReceipt | null {
  try {
    const raw = sessionStorage.getItem(RECEIPT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(RECEIPT_KEY);
    return JSON.parse(raw) as BuildRefreshReceipt;
  } catch {
    return null;
  }
}

/**
 * The kill switch as the host reports it RIGHT NOW, or `null` if unreadable.
 *
 * The flag reaches a tab at boot, through the runtime config. But a host's env
 * only changes with a redeploy, and the tabs that matter are the ones already
 * open when that happens: they booted with the switch off and would hand their
 * next page change to the very next build. So it is re-read at the one moment
 * it counts — when a newer build is first seen, before anything is armed. The
 * request goes to the live deployment, whatever build this tab is running.
 */
async function fetchKillSwitch(): Promise<boolean | null> {
  try {
    const res = await fetch('/api/runtime-config', {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    const flag = (body as { webBuildRefreshDisabled?: unknown } | null)
      ?.webBuildRefreshDisabled;
    return typeof flag === 'boolean' ? flag : null;
  } catch {
    return null;
  }
}

async function fetchLiveBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) return null;
    // A host without the file answers with the SPA fallback (HTML); that is
    // "unknown", never "stale".
    const body: unknown = await res.json();
    const id = (body as { buildId?: unknown } | null)?.buildId;
    return typeof id === 'string' && id ? id : null;
  } catch {
    return null;
  }
}

/**
 * Start watching for a new deploy. Call once at app entry. Returns a teardown
 * (used by tests).
 */
export function installWebBuildRefresh(
  options: InstallOptions = {}
): () => void {
  if (installed || !currentBuildId || Capacitor.isNativePlatform()) {
    return () => undefined;
  }
  installed = true;
  isBusy = options.isBusy ?? (() => false);

  let lastCheckAt = 0;
  let checking = false;
  let hiddenSince: number | null =
    document.visibilityState === 'hidden' ? Date.now() : null;

  const check = async () => {
    const now = Date.now();
    if (checking || now - lastCheckAt < MIN_CHECK_GAP_MS) return;
    checking = true;
    lastCheckAt = now;
    try {
      const live = await fetchLiveBuildId();
      if (!live || live === currentBuildId || live === liveBuildId) return;
      // Unreadable leaves the switch as it was: a blip must neither arm a
      // refresh the host has switched off nor disarm one it has not.
      const disabled = await fetchKillSwitch();
      if (disabled !== null) setWebBuildRefreshEnabled(!disabled);
      liveBuildId = live;
      if (alreadyReloadedFor(live)) {
        warn('Still stale after reloading for this build — not retrying', {
          currentBuildId,
          target: live,
        });
      }
    } finally {
      checking = false;
    }
  };

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      hiddenSince = Date.now();
      return;
    }
    const away = hiddenSince === null ? 0 : Date.now() - hiddenSince;
    hiddenSince = null;
    void check().then(() => {
      // They have only just come back, so nothing is in progress yet — unless
      // they left it that way, which is what the checks are for.
      if (
        away < IDLE_RETURN_MS ||
        idleReturnPages.size === 0 ||
        blocked() ||
        isTyping()
      ) {
        return;
      }
      try {
        if (claimLiveBuild('idle_return')) {
          window.location.replace(freshUrl(window.location.href));
        }
      } catch (error) {
        logError('app.buildRefresh', error, { feature: 'app' });
      }
    });
  };
  const onFocus = () => void check();

  // Visible tabs only: a hidden tab cannot act on the answer, and it re-checks
  // the moment it is shown.
  const pollTimer = setInterval(() => {
    if (document.visibilityState === 'visible') void check();
  }, POLL_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onFocus);
  window.addEventListener('online', onFocus);

  void check();

  return () => {
    clearInterval(pollTimer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('online', onFocus);
    holds.clear();
    idleReturnPages.clear();
    installed = false;
    enabled = true;
    liveBuildId = null;
    navigatingTo = null;
    pendingHandOff = null;
    isBusy = () => false;
  };
}
